import { AfterViewInit, Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OpportunitiesService } from '../services/opportunities.service';
import { SurplusLinesRegistrationModel } from '../models/surplus-lines-registration.model';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { SurplusLinesApplicationOtherInsurance, SurplusLinesOpportunitySnapshot, SurplusLinesApplicant } from '../models/surplus-lines-opportunity-snapshot.model';
import { OpportunitySnapshotsService } from '../services/opportunity-snapshots.service';
import { AuthService } from '../services/auth.service';
import { ConfigService } from '../services/config.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ChangeDetectorRef } from '@angular/core';
import { catchError, concatMap, debounceTime, finalize, map, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { CompassQuoteOptions, EmailCodeResult } from '../models/surplus-lines-opportunity.model';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  errorOverride: boolean = false;
  paymentVisible: boolean = false;
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isControlInvalid = !!(control && control.invalid && (control.dirty || control.touched || (form && form.submitted)));
    return this.errorOverride || isControlInvalid;
  }
}

export class MyNumericStateMatcher implements ErrorStateMatcher {
  errorOverride: boolean = false;
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    var isSubmitted = form && form.submitted;
    if (this.errorOverride && control) {
      isSubmitted = true;
    }
    if(control && (!control.value || (Number(control.value) <= 0)) && (control.dirty || control.touched || isSubmitted)){
      return true;
    }
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

export class MyEmailStateMatcher implements ErrorStateMatcher {
  errorOverride: boolean = false;
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    var isSubmitted = form && form.submitted;
    if (this.errorOverride && control) {
      isSubmitted = true;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (control && (!control.value || !emailRegex.test(control.value)) && (control.dirty || control.touched || isSubmitted)) {
      return true;
    }
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

export class MyErrorDateStateMatcher implements ErrorStateMatcher {
  errorOverride: boolean = false;
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    var isSubmitted = form && form.submitted;
    if (this.errorOverride && control) {
      isSubmitted = true;
    }

    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

type Step =
  | 'firstQuestion' | 'sponsorToken' | 'login' | 'previousEnrollment' | 'expiredQuote' | 'previouslySubmitted'
  | 'overrideEnrollment' | 'overridePrevious' | 'existingUser' | 'newToken' | 'emailSent'
  | 'noCode' | 'sendAnotherCode' | 'validateCodeNewToken' | 'validateCode'
  | 'emailNotFound' | 'invalidCode' | 'startOver' | 'address' | 'name' | 'birthdate'
  | 'earnedIncome' | 'otherInsurance' | 'yesDisabilty' | 'moreDisability' | 'noDisabilty'
  | 'emailPhone' | 'quote';

@Component({
  selector: 'app-application',
  standalone: false,
  templateUrl: './application.component.html',
  styleUrls: ['./application.component.css']
})
export class ApplicationComponent implements OnInit, AfterViewInit {
  @ViewChildren('code1, code2, code3, code4, code5, code6') inputs!: QueryList<ElementRef>;

  private stepIntent$ = new Subject<Step>();
  private email$ = new BehaviorSubject<string>('');
  private destroy$ = new Subject<void>();

  sponsorTokenControl = new FormControl('', { nonNullable: true });
  matcherNumeric: MyNumericStateMatcher = new MyNumericStateMatcher();
  matcherEmail: MyEmailStateMatcher = new MyEmailStateMatcher();
  matcherDate: MyErrorDateStateMatcher = new MyErrorDateStateMatcher();
  matcher1: MyErrorStateMatcher = new MyErrorStateMatcher();
  matcher2: MyErrorStateMatcher = new MyErrorStateMatcher();
  matcher3: MyErrorStateMatcher = new MyErrorStateMatcher();
  matcher4: MyErrorStateMatcher = new MyErrorStateMatcher();
  matcher5: MyErrorStateMatcher = new MyErrorStateMatcher();
  percent = 0;
  step: Step = 'login';
  email: string = '';
  code: string = '';
  code1Value: string = '';
  code2Value: string = '';
  code3Value: string = '';
  code4Value: string = '';
  code5Value: string = '';
  code6Value: string = '';
  firstName: string = '';
  lastName: string = '';
  address: string = '';
  birthdate: any;
  gender: string = '';
  occupation: string = '';
  earnedIncome: string = '';
  aptunit: string = '';
  phone: string = '';
  insuranceType: string = '';
  monthlyAmount: number | null = null;
  insuringCompany: string = '';
  whoPays: string = '';
  city: string = '';
  state: string = '';
  zip: string = '';
  otherInsurances: SurplusLinesApplicationOtherInsurance[] = [];
  typing: boolean = true;
  specialtyCodes: any[] = [];
  incomeTooLow: boolean = false;
  snackBarConfig: MatSnackBarConfig = {
    horizontalPosition: 'center',
    verticalPosition: 'top',
    duration: 5000
  };
  minYear: number = (new Date()).getFullYear() - 65;
  minDate = new Date(this.minYear, new Date().getMonth(), new Date().getDate()+1);
  maxYear: number = (new Date()).getFullYear() - 30;
  maxDate = new Date(this.maxYear, new Date().getMonth(), new Date().getDate());
  opportunityName: string = ''
  sponsorToken: string = '';
  invalidBirthdate: boolean = false;
  registerOverride: boolean = false;
  validateCode: Step = 'validateCode';

  stepText: { [key: string]: string } = {
    'firstQuestion': `Welcome! Have you started a quote with us before that you'd like to continue?`,
    'sponsorToken': `Great! To get pricing we need the <strong>Sponsor Token</strong> that was provided to your employer. It's like a code word so we know which coverage options you're eligible for.`,
    'previousEnrollment': `We found an active quote already tied to your email address.  Would you like to continue your prior quote or continue your current quote?`,
    'expiredQuote': `Welcome back! We see that you have a quote that has expired. Press continue to override your previous enrollment.`,
    'previouslySubmitted': `We found a submitted application with that email address.  We cannot continue with a new quote until your application is fully reviewed by our underwriting team.  We've emailed you a simple message indicating the status of your application.  If you have questions, please reach out.`,
    'login': `Let's find your information. Please provide the email address you entered during the quoting process.`,
    'emailSent': `Thanks! We found a quote tied to that email address. To keep your information safe, we've sent a code to your email. Please enter the code below to continue. The code will expire in 5 minutes.`,
    'newToken': `It looks like you are a returning user using a new token.  To keep your information safe, we've sent a code to your email.  Please enter the code below to continue.  The code will expire in 5 minutes.`,
    'overridePrevious': `We are ready to update your Application data, to keep your information safe, we've sent a code to your email. Please enter the code below to continue. The code will expire in 5 minutes.`,
    'emailNotFound': `We didn't find an active quote tied to that email address. Would you like to try a different email address?`,
    'noCode': `The email comes from info@mgis.com. Perhaps it got filtered to a junk or other folder? If you can't find it, starting a new quote is pretty quick, but will require your Employer's Sponsor Token again. Please enter the code below to continue.`,
    'sendAnotherCode': `We've sent a new code to your email. Please enter the code below to continue. The code will expire in 5 minutes.`,
    'validateCode': `Code validated! Let's get started.                                     `,
    'invalidCode': `The code you entered is invalid. You can try again or send a new one.`,
    'name': `High Limits Disability Insurance is designed for high-income earners whose individual disability and group long-term disability insurance don't provide sufficient income protection. We're excited to partner with <strong>${this.opportunityName}</strong> in offering this coverage to you. If you have additional questions, you can use the chat feature in the bottom-right corner. Otherwise, let's get you a quote!`,
    'address': `What's your home address?                                                        `,
    'birthdate': `Thanks! Now let's get some of the basics about you:             `,
    'earnedIncome': `What is your combined Earned Income from the prior year's K-1s, W-2s and 1099s?`,
    'otherInsurance': 'Please list any other disability insurance that you have currently or that you\'re applying for.',
    'yesDisabilty': 'Please enter the below details for your existing monthly disability insurance. If you have more than one, we\'ll add them one at a time.',
    'moreDisability': 'Do you have any other disability insurance other than those listed below?',
    'emailPhone': 'What is your email address and phone number?                      ',
    'existingUser': `You Have an account with us already but you do not have any High Limits Quotes, to start your quote please enter the code you have recieved in your email. The code will expire in 5 minutes.`,
    'quote': 'Thank you! Give us a moment while we generate your quote.',
    }

  

constructor(
  private _opportunitiesService: OpportunitiesService, 
  private _router: Router,
  private _snackBar: MatSnackBar,
  private _activatedRouter: ActivatedRoute,
  private _oss: OpportunitySnapshotsService,
  private _authService: AuthService,
  private _configService: ConfigService,
  private cdr: ChangeDetectorRef
) {
  this.step = 'firstQuestion';
}

  ngOnInit() {
    this._oss.getSpecialtyCodes().subscribe(response => {
      this.specialtyCodes = response.Specialties;
    });

    this._configService.getConfig().subscribe(config => {
    });

    this.stepIntent$
    .pipe(
      debounceTime(50),
      switchMap(step =>
        this.runStep(step).pipe(
          tap(() => {
            this.step = step;
            this.typing = true;
          }),
          catchError(err => {
            this.handleError(err, step);
            this.typing = false;
            return EMPTY;
          }),
        )
      )
    )
    .subscribe();
  }

  handleError(err: any, step: string){
    console.log(err.toString() + step)
  }

  ngAfterViewInit(): void {
    this.typeEffect();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startApplication() {
  const sponsorToken = this.sponsorTokenControl.value.toLowerCase();
  
  this._opportunitiesService.validateToken(sponsorToken).subscribe({
    next: (response) => {
      if (response.Status === '1') {
        this.matcher1.errorOverride = true;
        this.sponsorTokenControl.setErrors({ invalidToken: true });
        return;
      }

      this.matcher1.errorOverride = false;
      this.sponsorTokenControl.setErrors(null);

      localStorage.setItem('sponsorToken', sponsorToken.toLowerCase());
      localStorage.setItem('sponsorTokenInfo', JSON.stringify(response));
      localStorage.setItem('opportunityName', response.OpportunityByName);
      this.opportunityName = response.OpportunityByName;
      this.stepText['name'] = `We're proud to partner with <strong>${this.opportunityName}</strong> to offer High Limits Disability Insurance for high-income professionals.  If you have questions, use the chat feature below. Otherwise, start your application and quote now.`;
      this.gotoStep('name');
    },
    error: () => {
      this.matcher1.errorOverride = true;
      this.sponsorTokenControl.setErrors({ invalidToken: true });
    }
  });
}

  addOtherInsuranceData(): void {
    if (this.monthlyAmount && this.monthlyAmount > 0) {
      var otherInsurance = new SurplusLinesApplicationOtherInsurance();
      otherInsurance.Type = this.insuranceType;
      otherInsurance.MonthlyAmount = String(this.monthlyAmount);
      otherInsurance.Source = this.insuringCompany;
      otherInsurance.Paid = this.whoPays;
      this.otherInsurances.push(otherInsurance);
      this.insuranceType = '';
      this.monthlyAmount = null;
      this.insuringCompany = '';
      this.whoPays = '';
    }
  }

  nextCode(previousInput: HTMLInputElement, currentInput: HTMLInputElement, nextInput: HTMLInputElement): void {
    if (currentInput.value.length > 0) {
      nextInput.focus();
    }
    if (currentInput.value.length == 0 && nextInput.value.length == 0) {
      previousInput.focus();
    }
  }

  typeEffect() {
    return this.typeLine().subscribe({
      complete: () => {
        this.typing = false;
      }
    });
  }


  typeLine(): Observable<void> {
    return new Observable<void>(observer => {
      const currentStep = this.step;
      const text = this.stepText[currentStep] ?? '';
      const typingElement = document.querySelector('.typing-effect') as HTMLElement | null;
      if(typingElement){
        this.typing = true;
      }
      else {
        this.typing = false;
        observer.complete();
        return;
      }

      typingElement.innerHTML = '';

      const lines = text.split('\n');
      let lineIndex = 0;
      let index = 0;

      const charDelay = 5;
      const lineDelay = 500;  

      let timerId: any = null;
      let cancelled = false;

      const clearTimer = () => {
        if (timerId !== null) {
          clearTimeout(timerId);
          timerId = null;
        }
      };

      const done = () => {
        this.typing = false;
        clearTimer();
        observer.complete();
      };

      const tick = () => {

        if (lineIndex >= lines.length) {
          return done();
        }

        const line = lines[lineIndex];

        if (index < line.length) {
          if (line.charAt(index) === '<') {
            let tag = '';
            while (index < line.length && line.charAt(index) !== '>') {
              tag += line.charAt(index);
              index++;
            }
            if (index < line.length && line.charAt(index) === '>') {
              tag += '>';
              index++;
            }
            typingElement.innerHTML += tag;
          } else {
            typingElement.innerHTML += line.charAt(index);
            index++;
          }

          timerId = setTimeout(tick, charDelay);
        } else {
          typingElement.innerHTML += '<br>';
          index = 0;
          lineIndex++;
          timerId = setTimeout(tick, lineDelay);
        }
      };

      tick();

      return () => {
        cancelled = true;
        clearTimer();
        this.typing = false;
      };
    });
  };

  gotoStep(step: Step): void {
    if (this.typing) {
      this._snackBar.open('Please wait for instructions...', 'Close', { duration: 2000 });
      return;
    }
    this.typing = true;
    this.stepIntent$.next(step);
  }

  private runStep(step: Step) : Observable<void | EmailCodeResult | CompassQuoteOptions | SurplusLinesOpportunitySnapshot | boolean | string |null>{
    switch (step) {
      case 'firstQuestion':
      case 'sponsorToken':
      case 'login':
        return this.simpleStep(step, { clearEmail: true, clearCode: true, percent: 0 });

      case 'previousEnrollment':
      case 'expiredQuote':
        return this.simpleStep(step, { clearCode: true, percent: 0, type: false });

      case 'previouslySubmitted':
        return this.simpleStep(step, { clearCode: true, percent: 0 });

      case 'overrideEnrollment':
        return this.simpleStep(step, { clearCode: true, percent: 0, after: () => this.overrideEnrollment() });
      case 'overridePrevious':
      case 'existingUser':
      case 'newToken':
      case 'emailSent':
      case 'noCode':
      case 'sendAnotherCode':
        return this.sendCodeFlow(step);

      case 'validateCodeNewToken':
        return this.validateCodeFlow({ isNewToken: true });

      case 'validateCode':
        return this.validateCodeFlow({ isNewToken: false });

      case 'emailNotFound':
      case 'invalidCode':
        return this.simpleStep(step, { percent: 0 });

      case 'startOver':
        return of(null).pipe(
          tap(() => {
            localStorage.clear();
            this._router.navigate(['/landing-page']);
            return EMPTY;
          })
        );
        
      case 'address':
        return this.requireAll([
          { ok: !!this.lastName, mark: () => (this.matcher2.errorOverride = true) },
          { ok: !!this.firstName, mark: () => (this.matcher1.errorOverride = true) },
        ]).pipe(
          switchMap(valid => valid ? this.simpleStep(step, { percent: 5 }) : EMPTY)
        );

      case 'name':
        return of(this.code1Value + this.code2Value + this.code3Value + this.code4Value + this.code5Value + this.code6Value)
          .pipe(
            map(userEnteredCode => (this.code === userEnteredCode || this.sponsorToken.length > 0)),
            switchMap(ok => ok ? this.simpleStep(step, { percent: 0 }) : this.simpleStep('invalidCode', { percent: 0 }))
          );

      case 'birthdate':
        return this.requireAll([
          { ok: !!this.address,  mark: () => (this.matcher1.errorOverride = true) },
          { ok: !!this.city,     mark: () => (this.matcher2.errorOverride = true) },
          { ok: !!this.state,    mark: () => (this.matcher3.errorOverride = true) },
          { ok: !!this.zip,      mark: () => (this.matcher4.errorOverride = true) },
        ]).pipe(
          switchMap(valid => valid ? this.simpleStep(step, { percent: 7 }) : EMPTY)
        );

      case 'earnedIncome':
        return this.requireAll([
          { ok: !!this.birthdate, mark: () => (this.matcherDate.errorOverride = true) },
          { ok: !!this.gender,    mark: () => (this.matcher2.errorOverride = true) },
          { ok: !!this.occupation,mark: () => (this.matcher3.errorOverride = true) },
        ]).pipe(
          switchMap(valid => valid ? this.simpleStep(step, { percent: 10 }) : EMPTY)
        );

        case 'otherInsurance':
          return of(this.earnedIncome).pipe(
            map(v => Number.parseFloat((v ?? '').replace(/[^0-9.]/g, ''))),
            switchMap(parsedIncome =>
              (!Number.isNaN(parsedIncome) && parsedIncome > 30000)
                ? this.simpleStep(step, { percent: 11 })
                : of(null).pipe(
                    tap(() => { this.matcher1.errorOverride = true; this.cdr.markForCheck(); }),
                    switchMap(() => EMPTY)
                  )
            )
          );

      case 'yesDisabilty':
      case 'moreDisability':
        return (step === 'moreDisability'
          ? this.requireAll([
              { ok: !!this.insuranceType,   mark: () => (this.matcher1.errorOverride = true) },
              { ok: !!this.monthlyAmount && this.monthlyAmount > 0, mark: () => (this.matcher2.errorOverride = true) },
              { ok: !!this.insuringCompany, mark: () => (this.matcher3.errorOverride = true) },
              { ok: !!this.whoPays,         mark: () => (this.matcher4.errorOverride = true) },
            ])
          : of(true)
        ).pipe(
          tap(() => { this.matcher1.errorOverride = this.matcher2.errorOverride = this.matcher3.errorOverride = this.matcher4.errorOverride = false; }),
          tap(valid => { if (valid) this.addOtherInsuranceData(); }),
          switchMap(valid => valid ? this.simpleStep(step, { percent: step === 'yesDisabilty' ? 13 : 14 }) : EMPTY)
        );

      case 'noDisabilty':
        return this.simpleStep(step, { percent: 15 });

      case 'emailPhone':
        return of(null).pipe(
          tap(() => { this.matcher1.errorOverride = false; this.matcher2.errorOverride = false; }),
          switchMap(() => this.simpleStep(step, { percent: 20 }))
        );

      case 'quote':
        return this.quoteFlow();

      default:
        return this.simpleStep('login', { percent: 0 });
    }
  }

  private simpleStep(
  step: Step,
  opts?: { clearEmail?: boolean; clearCode?: boolean; percent?: number; type?: boolean; after?: () => void }
  ): Observable<void> {
    return of(void 0).pipe(
      tap(() => {
        if (opts?.clearEmail) this.email = '';
        if (opts?.clearCode)  this.code = '';
        this.step = step;
        this.percent = opts?.percent ?? 0;
        opts?.after?.();
      }),
      finalize(() => {
        if (opts?.type !== false) this.typeEffect();
      })
    );
  }

  private requireAll(items: { ok: boolean; mark: () => void }[]) {
    const allOk = items.every(i => i.ok);
    if (!allOk) items.forEach(i => { if (!i.ok) i.mark(); });
    return of(allOk);
  }

  private sendCodeFlow(targetStep: Step): Observable<void | EmailCodeResult> {
    return of(null).pipe(
      withLatestFrom(this.email$),
      map(([_, email]) => email || this.email),
      switchMap(email => {
        if (!email) {
          this.matcher1.errorOverride = true;
          return EMPTY;
        }
        this.matcher1.errorOverride = false;

        return this._opportunitiesService.emailExists(email).pipe(
          switchMap(exists => exists
            ? this._opportunitiesService.sendEmail(email).pipe(
                tap(() => {
                  this.step = targetStep;
                  this._snackBar.open('Email Sent!', 'Close', this.snackBarConfig);
                }),
                catchError(err => {
                  this._snackBar.open(err?.error ?? 'Failed to send email', 'Close', this.snackBarConfig);
                  this.step = 'emailNotFound';
                  return EMPTY;
                })
              )
            : this.simpleStep('emailNotFound')
          ),
          catchError(err => {
            this.step = 'emailNotFound';
            this.simpleStep('emailNotFound')
            return EMPTY;
          }),
          finalize(() => this.typeEffect())
        )
      })
    );
  }

  exit(){
    this._router.navigate(['/landing-page']);
  }

  rerouteCode(code: Step){
    this.validateCode = code;
  }

  onPaste(event: ClipboardEvent) {
    const pastedText = event?.clipboardData?.getData('text');
    const values = pastedText?.split('');
    this.code1Value = values ? values[0] : '';
    this.code2Value = values ? values[1] : '';
    this.code3Value = values ? values[2] : '';
    this.code4Value = values ? values[3] : '';
    this.code5Value = values ? values[4] : '';
    this.code6Value = values ? values[5] : '';
  }

  private validateCodeFlow(opts: { isNewToken: boolean }) {
    this.typing = true;
    return of(null).pipe(
      map(() => {
        const code = [this.code1Value, this.code2Value, this.code3Value, this.code4Value, this.code5Value, this.code6Value].join('');
        return { code, email: this.email };
      }),
      switchMap(({ code, email }) => {
        const created = this._opportunitiesService.CurrentEmailCode?.Created;
        const createdAt = created ? new Date(created) : null;
        const valid = this._opportunitiesService.checkCode(code, email)
          && !!createdAt
          && (Date.now() - createdAt.getTime()) < 300_000;

        if (!valid) {
          localStorage.setItem('validatedCode', 'false');
          this.step = 'invalidCode';
          return this.simpleStep('invalidCode');
        }

        localStorage.setItem('validatedCode', 'true');

        return this._authService.login(email, code).pipe(
          concatMap(() => opts.isNewToken ? this.newTokenChain(email, code) : this.existingTokenChain()),
          catchError(err => {
            var msg = err?.error?.ErrorDescription
              ? (err.error.ErrorDescription === 'The specified user account has been suspended'
                ? 'Your account has been temporarily locked due to multiple failed login attempts.'
                : err.error.ErrorDescription)
              : (err?.message ?? 'Login failed. Please try again.');
             if (msg == "Please check that your email and password are correct."){
              localStorage.setItem('validatedCode', 'false');
              msg = 'The code you entered is invalid.';
             }
            this._snackBar.open(msg, 'Close', this.snackBarConfig);
            return EMPTY;
          }),
          finalize(() => this.typeEffect())
        );
      })
    );
  }

  private newTokenChain(email: string, code: string) {
    return this._oss.getApplicantInfo().pipe(
      tap(applicantInfo => {
        applicantInfo.UserId      = this._oss.CurrentUserId;
        applicantInfo.FirstName   = this.firstName;
        applicantInfo.LastName    = this.lastName;
        applicantInfo.Email       = email;
        applicantInfo.Phone       = this.phone;
        applicantInfo.Gender      = this.gender;
        applicantInfo.BirthDate   = this.birthdate.toLocaleDateString();
        applicantInfo.AnnualIncome= this.earnedIncome;
        applicantInfo.Address1    = this.address;
        applicantInfo.Address2    = this.aptunit;
        applicantInfo.City        = this.city;
        applicantInfo.State       = this.state;
        applicantInfo.PostalCode  = this.zip;
        applicantInfo.SpecialtyCode = this.occupation;
        applicantInfo.Token       = localStorage.getItem('sponsorToken') as string;
      }),
      concatMap(applicantInfo => this._oss.useIPHLToken(applicantInfo)),
      tap(resp => localStorage.setItem('TokenInfo', JSON.stringify(resp))),
      concatMap(() => this._oss.getCompassOpportunity()),
      concatMap(() => {
        if (this._oss.currentCompassOpportunity?.CompassId) {
          const snap = new SurplusLinesOpportunitySnapshot();
          snap.OpportunityId   = this._oss.currentCompassOpportunity.CompassId!;
          snap.HasOtherInsurance = this.otherInsurances.length > 0;
          snap.OtherInsurance  = this.otherInsurances;
          snap.InsuredId       = this._oss.currentCompassOpportunity.InsuredId;
          snap.SponsorToken    = localStorage.getItem('sponsorToken') as string;
          return this._oss.addOpportunitySnapshot(snap).pipe(
            tap(() => this._router.navigate(['/quote']))
          );
        }
        return EMPTY;
      })
    );
  }

  private existingTokenChain() {
    const userId = this._oss.CurrentUserId;
    return this._oss.getApplicantInfo(userId).pipe(
      concatMap(applicantInfo => {
        const token = (localStorage.getItem('sponsorToken') || '');
        if(applicantInfo == null) {
          throw new Error('You have an account but you have not started a quote, please start an application with a registration token.');
        }
        return this._oss.currentOppSnapByApplicantId(applicantInfo.Id, token).pipe(
          concatMap(oss => this._oss.getCompassOpportunity().pipe(
            tap(compassOpportunity => {
              applicantInfo.Token           = oss.SponsorToken;
              applicantInfo.CompassId       = oss.OpportunityId;
              applicantInfo.OwnerId         = compassOpportunity.OwnerId;
              applicantInfo.OpportunityRole = compassOpportunity.Role;
              applicantInfo.JobTitle        = '';
              localStorage.setItem('sponsorToken', oss.SponsorToken.toLowerCase());
              localStorage.setItem('opportunityName', compassOpportunity.SponsorName);
              this.opportunityName = compassOpportunity.SponsorName;
            }),
            concatMap(() => this._oss.returnIPHLToken(applicantInfo)),
            tap(() => {
              this._router.navigate(['/quote']);
              this.step = 'validateCode';
            })
          ))
        );
      }),
      catchError(err => {
        this._snackBar.open(err?.message ?? 'An error occurred.', 'Close', this.snackBarConfig);
        return EMPTY;
      })
    );
  }

  overrideEnrollment(){
    this._opportunitiesService.emailExists(this.email).subscribe({
    next: (response) => { 
       this.percent = 25;
        var newUser = new SurplusLinesRegistrationModel();
        newUser.FirstName = this.firstName;
        newUser.LastName = this.lastName;
        newUser.Email = this.email;
        newUser.Phone = this.phone;

        var userEnteredCode = this.code1Value + this.code2Value + this.code3Value + this.code4Value + this.code5Value + this.code6Value;
        newUser.Token = localStorage.getItem('sponsorToken') as string;
        newUser.OpportunityRole = 'High Limit Disability Insurance';
        this._authService.login(this.email, userEnteredCode).subscribe({
          next: (login) => {
            this._oss.getApplicantInfo(this._oss.CurrentUserId).subscribe({
              next: (response) => {
                if (response) {
                  var applicantInfo = new SurplusLinesApplicant();
                  applicantInfo.Id = response.Id;
                  applicantInfo.UserId = this._oss.CurrentUserId;
                  applicantInfo.FirstName = this.firstName;
                  applicantInfo.LastName = this.lastName;
                  applicantInfo.Email = this.email;
                  applicantInfo.Phone = this.phone;
                  applicantInfo.Gender = this.gender;
                  applicantInfo.BirthDate = this.birthdate.toLocaleDateString()
                  applicantInfo.AnnualIncome = this.earnedIncome;
                  applicantInfo.Address1 = this.address;
                  applicantInfo.Address2 = this.aptunit;
                  applicantInfo.City = this.city;
                  applicantInfo.State = this.state;
                  applicantInfo.PostalCode = this.zip;
                  applicantInfo.SpecialtyCode = this.occupation;
                  applicantInfo.Token = localStorage.getItem('sponsorToken') as string;
                  
                  this._oss.useIPHLToken(applicantInfo).subscribe(resp => {
                    localStorage.setItem('TokenInfo', JSON.stringify(resp));
                    this._oss.getCompassOpportunity().subscribe(response => {
                      if (this._oss.currentCompassOpportunity && this._oss.currentCompassOpportunity.CompassId) {
                        var opportunitySnapshot = new SurplusLinesOpportunitySnapshot();
                        opportunitySnapshot.OpportunityId = this._oss.currentCompassOpportunity.CompassId as number;
                        opportunitySnapshot.HasOtherInsurance = this.otherInsurances.length > 0;
                        opportunitySnapshot.OtherInsurance = this.otherInsurances;
                        opportunitySnapshot.InsuredId = this._oss.currentCompassOpportunity.InsuredId
                        opportunitySnapshot.SponsorToken = localStorage.getItem('sponsorToken') as string;
                        
                        this._oss.addOpportunitySnapshot(opportunitySnapshot).subscribe(response => {
                          this._router.navigate(['/quote']);
                        });
                      }
                    });
                  });
                }
              },
            error: (error) => {
              if (error.error[''] && error.error[''][0]) {
                this._snackBar.open(error.error[''][0], 'Close', this.snackBarConfig);
              } else {
                this._snackBar.open(error.error, 'Close', this.snackBarConfig);
              }

              this.step = 'emailPhone';
            },
            complete: () => {
              this.typeEffect();
              this.typing = false;
            }
          });
        }})
      }
    })
  }

private quoteFlow() : Observable<void | boolean| string | SurplusLinesOpportunitySnapshot> {
  const invalid =
    !this.email ||
    !this.phone ||
    this.phone.length < 14;

  if (invalid) {
    this.matcherEmail.errorOverride = !this.email;
    this.matcher2.errorOverride = !this.phone || this.phone.length < 14;
    return EMPTY;
  }

  this.percent = 25;

  const newUser = new SurplusLinesRegistrationModel();
  newUser.FirstName = this.firstName;
  newUser.LastName  = this.lastName;
  newUser.Email     = this.email;
  newUser.Phone     = this.phone;
  newUser.Password  = `1${Math.random().toString(36).slice(-8)}!A`;
  newUser.Token     = localStorage.getItem('sponsorToken') as string;
  newUser.OpportunityRole = 'High Limit Disability Insurance';
  
  return this._opportunitiesService.ExistingUserWithNoApplication(this.email).pipe(
    switchMap(userId => {
      if (userId) {
        return this._opportunitiesService.AddApplicant(userId).pipe(
          tap(updated => {
            if (!updated) this._snackBar.open('an issue occured updating your account', 'Close', this.snackBarConfig);
          }),
          tap(() => this.stepIntent$.next('existingUser')) 
        );
      }
      return this._opportunitiesService.createAccount(newUser).pipe(
        switchMap(resp => {
          if (!resp) {
            this.step = 'previousEnrollment';
            this.stepIntent$.next('previousEnrollment');
            return EMPTY;
          }

          if ((resp as any).CompassPassword === '') {
            this.validateCode = 'validateCodeNewToken';
            this.stepIntent$.next('newToken');
          }

          const applicantInfo = this.buildApplicantInfoFromForm();

          return this._authService.login(newUser.Email, newUser.Password).pipe(
            concatMap(() => this._oss.useIPHLToken(applicantInfo)),
            tap(t => localStorage.setItem('TokenInfo', JSON.stringify(t))),
            concatMap(() => this._oss.getCompassOpportunity()),
            concatMap(() => {
              if (this._oss.currentCompassOpportunity?.CompassId) {
                const snap = new SurplusLinesOpportunitySnapshot();
                snap.OpportunityId   = this._oss.currentCompassOpportunity.CompassId!;
                snap.HasOtherInsurance = this.otherInsurances.length > 0;
                snap.OtherInsurance  = this.otherInsurances;
                snap.InsuredId       = this._oss.currentCompassOpportunity.InsuredId;
                snap.SponsorToken    = localStorage.getItem('sponsorToken') as string;

                return this._oss.addOpportunitySnapshot(snap).pipe(
                  tap(() => this._router.navigate(['/quote']))
                );
              }
              return EMPTY;
            })
          );
        }),
        catchError(error => {
          const text = error?.error?.text;
          let msg: string | null = null;

          if (text === 'Previously submitted') {
            this.step = 'previouslySubmitted';
            this.gotoStep('previouslySubmitted');
          } else if (text === 'Expired Quote') {
            this.step = 'expiredQuote';
            this.gotoStep('expiredQuote');
          } else {
            msg = error?.error?.['']?.[0] ?? error?.error ?? 'An error occurred.';
          }

          if (msg) {
            this._snackBar.open(msg, 'Close', this.snackBarConfig);
          }

          return EMPTY;
        }),
        finalize(() => {
          this.typeEffect();
        })
      );
    })
  );
}

private buildApplicantInfoFromForm(): SurplusLinesApplicant {
  const ai = new SurplusLinesApplicant();
  ai.UserId       = this._opportunitiesService.CurrentUser?.Id ?? this._oss.CurrentUserId;
  ai.FirstName    = this.firstName;
  ai.LastName     = this.lastName;
  ai.Email        = this.email;
  ai.Phone        = this.phone;
  ai.Gender       = this.gender;
  ai.BirthDate    = this.birthdate.toLocaleDateString();
  ai.AnnualIncome = this.earnedIncome;
  ai.Address1     = this.address;
  ai.Address2     = this.aptunit;
  ai.City         = this.city;
  ai.State        = this.state;
  ai.PostalCode   = this.zip;
  ai.SpecialtyCode= this.occupation;
  ai.Token        = localStorage.getItem('sponsorToken') as string;
  return ai;
}

  preventNonNumeric(event: KeyboardEvent) {
    const k = event.key;

    const allowed = ['Backspace','Delete','Tab','Enter','ArrowLeft','ArrowRight','Home','End'];
    if (allowed.includes(k)) return;

    if ((event.ctrlKey || event.metaKey) && ['a','c','v','x'].includes(k.toLowerCase())) {
      return;
    }

    if (/^\d$/.test(k)) return;
    event.preventDefault();
  }

  checkDate() {
    this.matcherDate.errorOverride = false;
    if (this.birthdate && (this.birthdate < this.minDate) || (this.birthdate > this.maxDate)) {
      this.invalidBirthdate = true;
    } else {
      this.invalidBirthdate = false;
    }
  }

  deleteInsurance(insurance: any): void {
    this.otherInsurances = this.otherInsurances.filter(i => i !== insurance);
    this.typing = false
  }

  gotoLanding(){
    localStorage.clear();
    this._router.navigate(['/']);
  }  

  gotoPreviousFromEmailPhone() {
    console.log('Other Insurances:', this.otherInsurances);
    const hasDisability = this.otherInsurances?.some(
      insurance => insurance.Source && insurance.MonthlyAmount
    );
    console.log('Has Disability:', hasDisability);
    console.log('Navigating to:', hasDisability ? 'moreDisability' : 'otherInsurance');

    this.gotoStep(hasDisability ? 'moreDisability' : 'otherInsurance');
  }

  get hasDisability(): boolean {
  return this.otherInsurances?.some(
    insurance => insurance.Source && insurance.MonthlyAmount
  ) ?? false;
  }

  get isOtherInsuranceValid(): boolean {
  return !!this.insuranceType &&
         !!this.monthlyAmount && this.monthlyAmount > 0 &&
         !!this.insuringCompany &&
         !!this.whoPays;
  }
  onNameInput(): void {
    if (this.firstName) {
      this.matcher1.errorOverride = false;
    }
    if (this.lastName) {
      this.matcher2.errorOverride = false;
    }
    if (this.state) {
      this.matcher3.errorOverride = false;
    }
    if (this.lastName) {
      this.matcher4.errorOverride = false;
    }
    if (this.gender) {
      this.matcher2.errorOverride = false;
    }
    if (this.occupation) {
      this.matcher3.errorOverride = false;
    }
    this.typing = false; 
  }


}
